(() => {
  'use strict';
  if (window.RTHCompactCart) return;
  const configNode = document.querySelector('[data-cg-cart-config]');
  if (!configNode) return;
  const config = JSON.parse(configNode.textContent);
  config.root = `${config.root.replace(/\/$/, '')}/`;
  const groupKey = '_rth_engraving_id';
  const roleKey = '_rth_engraving_role';
  const route = path => `${config.root}${path}`;
  let busy = false;

  async function request(path, body) {
    const response = await fetch(route(path), {
      method: body ? 'POST' : 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {Accept: 'application/json', ...(body ? {'Content-Type': 'application/json'} : {})},
      ...(body ? {body: JSON.stringify(body)} : {})
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.description || result.message || config.labels.cart_error);
    return result;
  }

  const read = () => request('cart.js');
  const groupLines = (cart, id) => cart.items.filter(line => line.properties?.[groupKey] === id);
  const parentLine = (cart, id) => groupLines(cart, id).find(line => line.properties[roleKey] === 'compact');
  const feeLines = (cart, id) => groupLines(cart, id).filter(line => line.properties[roleKey] === 'engraving');
  const change = (key, quantity) => request('cart/change.js', {id: key, quantity});

  function problems(cart) {
    const issues = [];
    const groups = new Set(cart.items.map(line => line.properties?.[groupKey]).filter(Boolean));
    for (const id of groups) {
      const parent = parentLine(cart, id);
      const fees = feeLines(cart, id);
      if (!parent || Number(parent.variant_id) !== config.compactVariant) {
        issues.push({id, reason: 'orphan'});
        continue;
      }
      const text = parent.properties.Engraving;
      const font = parent.properties.Font;
      if (typeof text !== 'string' || !text.trim() || text.length > 9 || !['Classic', 'Script'].includes(font)) {
        issues.push({id, reason: 'text'});
        continue;
      }
      if (fees.length !== 1 || Number(fees[0].variant_id) !== config.feeVariant ||
          fees[0].quantity !== parent.quantity ||
          fees[0].properties.Engraving !== text || fees[0].properties.Font !== font ||
          fees[0].parent_relationship?.parent_key !== parent.key) {
        issues.push({id, reason: 'fee'});
      }
    }
    return issues;
  }

  async function exclusive(work) {
    if (busy) throw new Error(config.labels.cart_busy);
    busy = true;
    try {return await work();} finally {busy = false;}
  }

  async function add({text = '', font = '', quantity = 1} = {}) {
    return exclusive(async () => {
      if (!Number.isInteger(quantity) || quantity < 1 || quantity > 99) throw new Error(config.labels.cart_error);
      if (!font) {
        await request('cart/add.js', {items: [{id: config.compactVariant, quantity}]});
        return read();
      }
      if (!text.trim() || text.length > 9 || !['Classic', 'Script'].includes(font)) throw new Error(config.labels.invalid_engraving);
      if (!config.feeAvailable) throw new Error(config.labels.fee_unavailable);
      const id = crypto.randomUUID();
      const properties = {Engraving: text, Font: font, [groupKey]: id, [roleKey]: 'compact'};
      try {
        try {
          await request('cart/add.js', {items: [{id: config.compactVariant, quantity, properties}]});
        } catch (error) {
          if (!parentLine(await read(), id)) throw error;
        }
        const parent = parentLine(await read(), id);
        if (!parent || parent.quantity !== quantity) throw new Error(config.labels.cart_error);
        try {
          await request('cart/add.js', {items: [{id: config.feeVariant, quantity,
            parent_line_key: parent.key,
            properties: {...properties, [roleKey]: 'engraving'}}]});
        } catch (error) {
          if (feeLines(await read(), id).length !== 1) throw error;
        }
        const cart = await read();
        if (problems(cart).some(issue => issue.id === id)) throw new Error(config.labels.cart_error);
        return cart;
      } catch (error) {
        try {
          const parent = parentLine(await read(), id);
          if (parent) await change(parent.key, 0);
          const remaining = groupLines(await read(), id);
          for (const line of remaining) await change(line.key, 0);
        } catch {
          throw new Error(config.labels.recovery_error);
        }
        throw error;
      }
    });
  }

  async function setQuantity(id, quantity) {
    return exclusive(async () => {
      if (!Number.isInteger(quantity) || quantity < 0 || quantity > 99) throw new Error(config.labels.cart_error);
      let cart = await read();
      const parent = parentLine(cart, id);
      if (!parent) throw new Error(config.labels.cart_error);
      cart = await change(parent.key, quantity);
      if (quantity > 0) {
        const updatedParent = parentLine(cart, id);
        if (!updatedParent) throw new Error(config.labels.cart_error);
        for (const fee of feeLines(cart, id)) {
          if (fee.quantity !== updatedParent.quantity) cart = await change(fee.key, updatedParent.quantity);
        }
      }
      if (problems(cart).some(issue => issue.id === id)) throw new Error(config.labels.cart_error);
      return cart;
    });
  }

  window.RTHCompactCart = {add, read, problems, setQuantity, config, get busy() {return busy;}};

  const cartRoot = document.querySelector('[data-cg-cart]');
  if (!cartRoot) return;
  const message = cartRoot.querySelector('[data-cg-cart-error]');
  function showError(error) {
    message.textContent = error?.message || '';
    message.hidden = !error;
  }
  function lock(value) {
    cartRoot.setAttribute('aria-busy', String(value));
    cartRoot.querySelectorAll('button').forEach(button => {button.disabled = value;});
  }
  async function refresh() {
    const response = await fetch(`${route('cart')}?sections=cart`, {cache: 'no-store', credentials: 'same-origin'});
    if (!response.ok) throw new Error(config.labels.cart_error);
    const sections = await response.json();
    const parsed = new DOMParser().parseFromString(sections.cart, 'text/html');
    const drawer = parsed.querySelector('cart-drawer');
    if (!drawer) throw new Error(config.labels.cart_error);
    cartRoot.querySelector('cart-drawer').replaceWith(drawer);
  }
  cartRoot.addEventListener('click', async event => {
    const button = event.target.closest('[data-cg-quantity]');
    if (!button) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (busy || cartRoot.getAttribute('aria-busy') === 'true') return;
    const item = button.closest('[data-cg-group]');
    const quantity = button.dataset.cgQuantity === 'remove' ? 0 : Number(item.dataset.quantity) + Number(button.dataset.cgQuantity);
    showError(null);
    lock(true);
    try {await setQuantity(item.dataset.cgGroup, quantity); await refresh();}
    catch (error) {showError(error); try {await refresh();} catch {}}
    finally {lock(false);}
  }, {capture: true});

  cartRoot.addEventListener('submit', async event => {
    if (!event.target.matches('#cart-form')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (busy || cartRoot.getAttribute('aria-busy') === 'true') return;
    lock(true);
    showError(null);
    try {
      const cart = await read();
      if (problems(cart).length) throw new Error(config.labels.checkout_error);
      window.location.assign(route('checkout'));
    } catch (error) {showError(error); lock(false);}
  }, {capture: true});
  read().then(cart => {if (problems(cart).length) showError(new Error(config.labels.checkout_error));}).catch(showError);
  window.addEventListener('pageshow', async event => {
    if (!event.persisted) return;
    lock(true);
    showError(null);
    try {
      await refresh();
      if (problems(await read()).length) throw new Error(config.labels.checkout_error);
    } catch (error) {showError(error);}
    finally {lock(false);}
  });
})();
